import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usePostSchedules,
  useGetSchedules,
  getProfilesById,
  getGetProfilesByIdQueryKey,
  getGetSchedulesQueryKey,
} from '@/api/control/control-plane/control-plane';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { DeleteScheduleDialog } from '@/components/profiles/schedules/DeleteScheduleDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Info, AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import { LoadingButton } from '@/components/LoadingButton';
import { useError } from '@/contexts/ErrorContext';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import type { ScheduleConfigRequest, ScheduleDto, DayOfWeek } from '@/api/control/missionImpactAPI.schemas';
import { useLanguage } from '@/contexts/LanguageContext';

type ScheduleType = ScheduleConfigRequest['$type'];

const daysOfWeekIndices: DayOfWeek[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * Step 5: Configure Quiet Time
 * Fifth step in the profile creation wizard
 */
export default function ConfigureQuietTimeStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();

  if (!orgId || !profileId) {
    logger.error(new Error('Missing orgId or profileId in route params'), {
      context: 'ConfigureQuietTimeStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const [scheduleType, setScheduleType] = useState<ScheduleType>('QuietTimeDaily');
  const [startHour, setStartHour] = useState('22');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('06');
  const [endMinute, setEndMinute] = useState('00');
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([5, 6, 0]); // Friday, Saturday, Sunday
  const [scheduleToDelete, setScheduleToDelete] = useState<ScheduleDto | null>(null);

  // Fetch profile to verify it exists
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    queryFn: async () => {
      const response = await getProfilesById(orgId, profileId);
      return response;
    },
    retry: 1,
  });

  // Fetch existing schedules
  const { data: schedulesData, isLoading: isLoadingSchedules } = useGetSchedules(orgId, profileId, {
    query: {
      enabled: !!orgId && !!profileId,
    },
  });

  const schedules: ScheduleDto[] = schedulesData?.data ?? [];
  const hasQuietTime = schedules.length > 0;

  // Get wizard steps with proper completion status
  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.QUIET_TIME, {
    [WIZARD_STEP_IDS.QUIET_TIME]: hasQuietTime,
  });

  // Redirect if profile doesn't exist or if setup is incomplete
  useEffect(() => {
    if (!isLoadingProfile && profileData?.data) {
      if (!profileData.data.source) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`);
      } else if (!profileData.data.target) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`);
      }
    } else if (!isLoadingProfile && !profileData?.data) {
      void navigate(`/dashboard/orgs/${orgId}/profiles/wizard/profile`);
    }
  }, [isLoadingProfile, profileData, navigate, orgId, profileId]);

  const calculateDurationInHours = (start: string, end: string): number => {
    const startMinutes = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1] ?? '0');
    const endMinutes = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1] ?? '0');

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes <= 0) {
      durationMinutes += 24 * 60;
    }

    return durationMinutes / 60;
  };

  /**
   * Format time string (HH:mm:ss) to display format (HH:mm)
   */
  const formatTime = (time: string): string => {
    if (!time) return '';
    const parts = time.split(':');
    return `${parts[0]}:${parts[1]}`;
  };

  /**
   * Get translated day names array
   */
  const getTranslatedDayNames = (): string[] => [
    t.wizard.quietTime.sunday,
    t.wizard.quietTime.monday,
    t.wizard.quietTime.tuesday,
    t.wizard.quietTime.wednesday,
    t.wizard.quietTime.thursday,
    t.wizard.quietTime.friday,
    t.wizard.quietTime.saturday,
  ];

  /**
   * Format schedule recurrence to display format
   */
  const formatScheduleRecurrence = (schedule: ScheduleDto): string => {
    if (schedule.$type === 'QuietTimeDaily') {
      return t.wizard.quietTime.typeDaily;
    } else {
      const translatedDayNames = getTranslatedDayNames();
      const dayNames = schedule.days.map((d: number) => translatedDayNames[d]).join(', ');
      return dayNames || t.wizard.quietTime.noDaysSelected;
    }
  };

  const startTimeStr = `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')}`;
  const endTimeStr = `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')}`;
  const duration = calculateDurationInHours(startTimeStr, endTimeStr);
  const isValidDuration = duration > 0 && duration <= 8;

  // Create schedule mutation
  const { mutate: createSchedule, isPending: isCreating } = usePostSchedules({
    mutation: {
      onSuccess: () => {
        toast.success(t.wizard.quietTime.quietTimeScheduleCreatedSuccessfully);
        void queryClient.invalidateQueries({ queryKey: getGetSchedulesQueryKey(orgId, profileId) });
        logger.trackEvent('QuietTime.Created', {
          orgId,
          profileId,
          scheduleType,
        });
      },
      onError: (error) => {
        handleApiError(error, { context: 'ConfigureQuietTimeStep.createSchedule', profile: profileData?.data });
      },
    },
  });

  const handleCreateSchedule = () => {
    const config =
      scheduleType === 'QuietTimeDaily'
        ? {
            $type: 'QuietTimeDaily' as const,
            startTime: `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')}:00`,
            endTime: `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')}:00`,
          }
        : {
            $type: 'QuietTimeWeekly' as const,
            days: selectedDays,
            startTime: `${startHour.padStart(2, '0')}:${startMinute.padStart(2, '0')}:00`,
            endTime: `${endHour.padStart(2, '0')}:${endMinute.padStart(2, '0')}:00`,
          };

    createSchedule({
      orgId,
      profileId,
      data: { config },
    });
  };

  if (isLoadingProfile || isLoadingSchedules) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">{t.wizard.quietTime.title}</h1>
        <p className="text-muted-foreground">{t.wizard.quietTime.description}</p>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.quietTime.cardTitle}</CardTitle>
          <CardDescription>{t.wizard.quietTime.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Information Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>{t.wizard.quietTime.infoAlert}</AlertDescription>
            </Alert>

            {/* Schedule Type */}
            <div className="space-y-2">
              <Label htmlFor="schedule-type">{t.wizard.quietTime.type}</Label>
              <Select value={scheduleType} onValueChange={(value) => { setScheduleType(value as ScheduleType); }}>
                <SelectTrigger id="schedule-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="QuietTimeDaily">{t.wizard.quietTime.typeDaily}</SelectItem>
                  <SelectItem value="QuietTimeWeekly">{t.wizard.quietTime.typeWeekly}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Days Selection for Weekly */}
            {scheduleType === 'QuietTimeWeekly' && (
              <div className="space-y-2">
                <Label>{t.wizard.quietTime.selectDays}</Label>
                <div className="space-y-2">
                  {daysOfWeekIndices.map((index) => {
                    const dayNames = [
                      t.wizard.quietTime.sunday,
                      t.wizard.quietTime.monday,
                      t.wizard.quietTime.tuesday,
                      t.wizard.quietTime.wednesday,
                      t.wizard.quietTime.thursday,
                      t.wizard.quietTime.friday,
                      t.wizard.quietTime.saturday,
                    ];
                    return (
                    <div key={index} className="flex items-center space-x-2">
                      <Checkbox
                        id={`day-${String(index)}`}
                        checked={selectedDays.includes(index)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDays([...selectedDays, index].sort());
                          } else {
                            setSelectedDays(selectedDays.filter((d) => d !== index));
                          }
                        }}
                      />
                      <label
                        htmlFor={`day-${String(index)}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {dayNames[index]}
                      </label>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            {/* Time Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.wizard.quietTime.startTime}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={startHour}
                    onChange={(e) => { setStartHour(e.target.value); }}
                    className="w-20"
                    placeholder={t.wizard.quietTime.hourPlaceholder}
                  />
                  <span>:</span>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={startMinute}
                    onChange={(e) => { setStartMinute(e.target.value); }}
                    className="w-20"
                    placeholder={t.wizard.quietTime.minutePlaceholder}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t.wizard.quietTime.endTime}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={endHour}
                    onChange={(e) => { setEndHour(e.target.value); }}
                    className="w-20"
                    placeholder={t.wizard.quietTime.hourPlaceholder}
                  />
                  <span>:</span>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={endMinute}
                    onChange={(e) => { setEndMinute(e.target.value); }}
                    className="w-20"
                    placeholder={t.wizard.quietTime.minutePlaceholder}
                  />
                </div>
              </div>
            </div>

            {/* Duration Validation */}
            {!isValidDuration && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t.wizard.quietTime.durationExceeded.replace('{hours}', duration.toFixed(1))}
                </AlertDescription>
              </Alert>
            )}

            {/* Add Schedule Button */}
            <LoadingButton
              type="button"
              variant="outline"
              onClick={handleCreateSchedule}
              disabled={!isValidDuration}
              className="w-full"
              isLoading={isCreating}
              loadingIcon={<Plus className="animate-spin" />}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t.wizard.quietTime.addQuietTimeBlock}
            </LoadingButton>

            {/* Configured Schedules Table */}
            {hasQuietTime && (
              <div className="space-y-2">
                <Label>{t.wizard.quietTime.configuredSchedules}</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.wizard.quietTime.recurrence}</TableHead>
                      <TableHead>{t.wizard.quietTime.startTime}</TableHead>
                      <TableHead>{t.wizard.quietTime.endTime}</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map((schedule) => (
                      <TableRow key={schedule.id}>
                        <TableCell>{formatScheduleRecurrence(schedule)}</TableCell>
                        <TableCell>{formatTime(schedule.startTime)}</TableCell>
                        <TableCell>{formatTime(schedule.endTime)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setScheduleToDelete(schedule); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t">
              <Button
                type="button"
                onClick={() => { void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/delete-mode`); }}
              >
                {t.wizard.quietTime.nextConfigureDeleteMode}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Schedule Dialog */}
      <DeleteScheduleDialog
        orgId={orgId}
        profileId={profileId}
        schedule={scheduleToDelete}
        open={!!scheduleToDelete}
        onOpenChange={(open) => { if (!open) setScheduleToDelete(null); }}
      />
    </div>
  );
}

